// The one sign-in flow for the running app. Real Supabase, or the fake when the build says so.
import { AuthFlow } from "./auth-flow";
import type { AuthApi } from "./auth-api";
import { practiceAs } from "@/practice/mode";

export const FAKE_AUTH = import.meta.env.VITE_FAKE_AUTH === "1";

let flow: Promise<AuthFlow> | null = null;

/** The flow, made once however many callers ask at the same time. */
export function getAuthFlow(): Promise<AuthFlow> {
  return (flow ??= makeFlow().catch((e: unknown) => {
    flow = null; // the code didn't load (no signal): the next caller tries again
    throw e;
  }));
}

/**
 * A guess before any sign-in code has loaded: has someone been signed in on this browser? Practice, the browser-test
 * build's session, Supabase's saved session, or a member remembered from a sign-in. Only decides what to fetch first.
 */
export function mightBeSignedIn(): boolean {
  try {
    if (practiceAs() || localStorage.getItem("sr2-fake-session")) return true;
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i) ?? "";
      if (k.startsWith("sr2-member-") || (k.startsWith("sb-") && k.endsWith("-auth-token"))) return true;
    }
  } catch {
    // storage blocked: fetch in the usual order
  }
  return false;
}

async function makeFlow(): Promise<AuthFlow> {
  let api: AuthApi;
  const practice = practiceAs();
  if (FAKE_AUTH || practice) {
    // Practice signs straight in as one of the made-up team; the browser-test build signs in like the real thing
    const { FakeAuth } = await import("./fake-auth");
    api = practice ? new FakeAuth({ as: practice }) : new FakeAuth({ persist: true, everyone: localStorage.getItem("sr2-fake-everyone") === "1" });
  } else {
    const { SupabaseAuth } = await import("./supabase-auth");
    api = new SupabaseAuth();
  }
  const made = new AuthFlow(api);
  void made.start();
  return made;
}
